import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { DEFAULT_HEAVY_OPACITY } from '@dat/datiq-components-pkg/utils';
import {
    isEmpty,
    noop
} from 'lodash';
import {
    IDatiqGraph,
    IDatiqGraphSeries
} from '../models';
import { DatiqAmcharts5TooltipBuilder } from './datiq-amcharts-5-tooltip.builder';
import { datiqAmcharts5SetColor } from './models';

export class DatiqAmCharts5SeriesLineBuilder {
    private _settings: Partial<am5xy.ILineSeriesSettings>;

    constructor() {}

    setDash(seriesData: IDatiqGraphSeries, series: am5xy.LineSeries) {
        seriesData?.dashed
            ? series?.strokes?.template.set('strokeDasharray', [3, 4])
            : noop();

        return this;
    }

    updateSeries(series: am5xy.LineSeries) {
        const settings: Partial<am5xy.ILineSeriesSettings> = this.build();

        series.setAll(settings);

        return this;
    }

    setTooltip(
        series: am5xy.LineSeries,
        root: am5.Root,
        seriesData: IDatiqGraphSeries,
        graph: IDatiqGraph
    ) {
        const tooltip: am5.Tooltip = new DatiqAmcharts5TooltipBuilder(
            series,
            root,
            seriesData,
            graph
        )
            .setLabel(graph, seriesData)
            .build();
        series.set('tooltip', tooltip);
        tooltip.set('dy', -60);
        tooltip.set('pointerOrientation', 'down');
        this.setTooltipPositioning(series);

        return this;
    }
    setTooltipPositioning(series: am5xy.LineSeries): void {
        const toolTip = series.get('tooltip');
        const chartHeaderOffset = 60;
        toolTip.events.on(
            'globalpointermove',
            (evt: am5.ISpritePointerEvent) => {
                const yPosition = evt?.target?.y() ?? 0;
                const height = evt?.target?.height();
                toolTip.set('pointerOrientation', 'down');
                toolTip.set('dy', -yPosition - height / 2 + chartHeaderOffset);
            }
        );
    }

    setBullets(
        series: am5xy.LineSeries,
        seriesData: IDatiqGraphSeries,
        root: am5.Root
    ): this {
        seriesData?.showBullets
            ? series.bullets.push((root) => {
                  return this.createBullet(root, series, seriesData);
              })
            : noop();

        return this;
    }

    private createBullet(
        root: am5.Root,
        series: am5xy.LineSeries,
        seriesData: IDatiqGraphSeries
    ): am5.Bullet {
        return am5.Bullet.new(root, {
            sprite: am5.Rectangle.new(root, {
                fill: am5.color(seriesData.color),
                width: 8,
                height: 8,
                centerX: am5.percent(50),
                centerY: am5.percent(50),
            }),
        });
    }

    // TODO: SEE IF THIS CAN FIX AREA :(
    setRanged(seriesData: IDatiqGraphSeries): this {
        this._settings = !!seriesData?.ranged?.openValueY?.length
            ? {
                  ...this._settings,
                  openValueYField: seriesData?.ranged?.openValueY,
                  valueYField: seriesData?.ranged?.valueY,
              }
            : { ...this._settings };

        return this;
    }

    setColor(seriesData: IDatiqGraphSeries) {
        this._settings = !!seriesData?.color?.length
            ? {
                  ...this._settings,
                  stroke: datiqAmcharts5SetColor(seriesData?.color),
              }
            : { ...this._settings };

        return this;
    }
    //TODO REMOVE
    setAreaColor(
        series: am5xy.LineSeries,
        seriesData: IDatiqGraphSeries
    ): this {
        if (!this.setFill(seriesData)) return this;

        series.set('fill', series.get('stroke'));
        series.fills.template.setAll({
            visible: true,
            fillOpacity: DEFAULT_HEAVY_OPACITY,
        });
        series.strokes.template.setAll({
            opacity: 0,
        });

        return this;
    }

    private getFill(
        series: am5xy.LineSeries,
        seriesData: IDatiqGraphSeries
    ): am5.Color {
        return !!seriesData?.color?.length
            ? datiqAmcharts5SetColor(seriesData?.color)
            : series?.get('stroke');
    }

    private setFill(seriesData: IDatiqGraphSeries) {
        return !isEmpty(seriesData?.ranged);
    }

    build(): Partial<am5xy.ILineSeriesSettings> {
        return this._settings;
    }
}
